<script setup lang="ts">
import { computed } from 'vue'
import plural from 'plural-ru'
import dayjs from '@/lib/dayjs'
import { arbitrary as formatNumberArbitrary } from '@/helpers/numberFormatters'
import { useScreen } from '@/composables/useScreen'
import duration from 'dayjs/plugin/duration'
import type { WebinarCalendarEvent, WebinarPageContext } from '../types'

const props = defineProps<{
  event: WebinarCalendarEvent
  currentDate: Date
  pageContext?: WebinarPageContext
}>()

const emit = defineEmits<{
  (e: 'action', payload: { type: 'event-open' | 'event-like' | 'event-purchase' | 'video-purchase'; id: number }): void
}>()

const { isMobile } = useScreen()

dayjs.extend(duration)

const timings = computed(() => {
  const diff = dayjs(props.event.end_time).diff(props.event.start_time, 'hour')
  return {
    date: props.event.start_time.format('D MMMM YYYY'),
    time: props.event.start_time.format('HH:mm'),
    duration: plural(diff, '%d час', '%d часа', '%d часов')
  }
})

const price = computed(() => formatNumberArbitrary(props.event.price))

const isPassed = computed(() => props.event.end_time.isBefore(props.currentDate))

const lockedInfoHtml = computed(() => {
  if (!props.event.me_locked) return ''

  const accessPolicies: string[] = []
  if (props.event.access_seminars.length) accessPolicies.push('s')
  if (props.event.access_acecourses.length) accessPolicies.push('c')
  if (props.event.access_categories.length) accessPolicies.push('a')

  if (accessPolicies.length !== 1) {
    return 'Извините, Вы не относитесь к приглашённым группам пользователей.'
  }

  if (props.event.access_categories) {
    const categories: string[] = []
    for (const code of props.event.access_categories.split(',')) {
      const category = props.pageContext?.categories?.find(item => item.code === code)
      if (category) categories.push(category.name)
    }
    return `На этот вебинар могут записаться только ${categories.join(', ')}`
  }

  const container = document.createElement('div')
  container.append('Станет доступно после прохождения ')

  if (props.event.access_seminars.length) {
    container.append(props.event.access_seminars.length > 1 ? 'одного из семинаров: ' : 'семинара ')
  }
  if (props.event.access_acecourses.length) {
    container.append(props.event.access_acecourses.length > 1 ? 'одного из курсов АС: ' : 'курса АС ')
  }

  const items = [
    ...props.event.access_seminars.map((id: number) => props.pageContext?.seminars?.find(item => item.id === id)),
    ...props.event.access_acecourses.map((id: number) => props.pageContext?.ace_courses?.find(item => item.id === id))
  ].filter(Boolean)

  for (const [index, seminar] of items.entries()) {
    if (!seminar) continue
    const link = document.createElement('a')
    link.innerText = seminar.name
    link.href = seminar.url
    link.target = '_blank'
    link.className = 'blue-text'
    container.append(link)

    if (index === items.length - 2) container.append(' или ')
    else if (index !== items.length - 1) container.append(', ')
  }

  return container.innerHTML
})

function rowClick() {
  emit('action', { type: 'event-open', id: props.event.id })
}

function openVideo() {
  if (!props.event.video_link) return
  window.open(props.event.video_link, '_blank')
}
</script>

<template>
  <!-- Mobile view -->
  <tr
    v-if="isMobile"
    class="webinar-calendar__list-event-row"
    :class="{ 'webinar-calendar__list-event-row_parents-allowed': event.access_policy === 'p' }"
    @click="rowClick"
  >
    <td class="webinar-calendar__list-event-mobile" colspan="3">
      <div class="d-flex justify-space-between mb-1">
        <span class="webinar-calendar__list-event-date"> {{ timings.date }} {{ timings.time }} </span>
        <span class="webinar-calendar__list-event-duration">
          <div v-text="timings.duration" />
        </span>
      </div>

      <div class="webinar-calendar__list-event-info">
        <span class="webinar-calendar__list-event-name" v-text="event.name" />
        <span class="webinar-calendar__list-event-author" v-text="event.coach_name" />

        <div v-if="event.price" class="webinar-calendar__list-event-price">
          {{ price }} <v-icon size="16">hand</v-icon>
        </div>
        <div v-else class="webinar-calendar__list-event-price">Бесплатно</div>

        <div>
          <span v-if="event.me_coach" class="webinar-calendar__list-event-badge"> Вы организатор </span>
        </div>
      </div>

      <div v-if="!isPassed" class="webinar-calendar__list-event-actions">
        <div class="webinar-calendar__list-passed">Вебинар прошёл</div>

        <v-button v-if="event.me_purchased || event.me_purchased_video" mode="primary block" @click.stop="openVideo">
          <v-icon class="mr-half">mdi-video</v-icon>
          Посмотреть
        </v-button>
        <v-button
          v-else-if="!event.me_locked && event.allow_purchase_video"
          mode="primary block"
          @click.stop="emit('action', { type: 'video-purchase', id: event.id })"
        >
          <v-icon class="mr-half">mdi-video</v-icon>
          Купить
        </v-button>
      </div>

      <div v-else class="webinar-calendar__list-event-actions">
        <v-button
          v-requires-auth
          class="webinar-calendar__list-fav-btn"
          mode="outlined block"
          @click.stop="emit('action', { type: 'event-like', id: event.id })"
        >
          <v-icon class="webinar-calendar__list-fav" :class="{ 'webinar-calendar__list-fav_active': event.me_liked }"
            >mdi-heart</v-icon
          >
        </v-button>

        <v-button
          v-if="event.me_coach"
          mode="outlined block"
          @click.stop="emit('action', { type: 'event-open', id: event.id })"
        >
          Открыть
        </v-button>
        <v-button
          v-else-if="event.me_purchased"
          mode="success block"
          @click.stop="emit('action', { type: 'event-open', id: event.id })"
        >
          Вы участник
        </v-button>
        <v-button
          v-else-if="!event.me_locked"
          v-requires-auth
          mode="primary block"
          @click.stop="emit('action', { type: 'event-purchase', id: event.id })"
        >
          {{ event.price ? 'Купить' : 'Записаться' }}
        </v-button>
      </div>

      <div v-if="event.me_locked">
        <span class="webinar-calendar__list-event-badge" v-html="lockedInfoHtml" />
      </div>
    </td>
  </tr>

  <!-- Desktop view -->
  <tr
    v-else
    class="webinar-calendar__list-event-row"
    :class="{ 'webinar-calendar__list-event-row_parents-allowed': event.access_policy === 'p' }"
    @click="rowClick"
  >
    <td>
      <div class="webinar-calendar__list-event-timings">
        <span class="webinar-calendar__list-event-date" v-text="timings.date" />
        <span class="webinar-calendar__list-event-time" v-text="timings.time" />
        <span class="webinar-calendar__list-event-duration">
          <div v-text="timings.duration" />
        </span>
      </div>
    </td>

    <td>
      <div class="webinar-calendar__list-event-body">
        <div class="webinar-calendar__list-event-info">
          <span class="webinar-calendar__list-event-name" v-text="event.name" />
          <span class="webinar-calendar__list-event-author" v-text="event.coach_name" />

          <div v-if="event.me_locked">
            <span class="webinar-calendar__list-event-badge" v-html="lockedInfoHtml" />
          </div>
        </div>
      </div>
    </td>

    <td>
      <!-- <div class="webinar-calendar__list-event-actions" v-if="isPassed">
        <div class="webinar-calendar__list-passed">Вебинар прошёл</div>

        <v-button
          v-if="event.me_purchased || event.me_purchased_video"
          mode="primary block"
          @click.stop="openVideo"
        >
          <v-icon class="mr-half">mdi-video</v-icon>
          Посмотреть
        </v-button>
        <v-button
          v-else-if="!event.me_locked && event.allow_purchase_video"
          mode="primary block"
          @click.stop="emit('action', { type: 'video-purchase', id: event.id })"
        >
          <v-icon class="mr-half">mdi-video</v-icon>
          Купить
        </v-button>
      </div> -->

      <div class="webinar-calendar__list-event-actions">
        <div v-if="event.price" class="webinar-calendar__list-event-price">
          {{ price }} <v-icon size="16">hand</v-icon>
        </div>
        <div v-else class="webinar-calendar__list-event-price">Бесплатно</div>

        <span v-if="isPassed" class="text-body-bold gray-text"> Завершен </span>

        <v-button
          :disabled="event.me_coach"
          class="webinar-calendar__list-fav-btn"
          mode="outlined"
          @click.stop="emit('action', { type: 'event-like', id: event.id })"
        >
          <v-icon class="webinar-calendar__list-fav" :class="{ 'webinar-calendar__list-fav_active': event.me_liked }"
            >mdi-heart</v-icon
          >
        </v-button>

        <v-button v-if="!event.me_locked && event.allow_purchase_video" mode="primary">
          <v-icon class="mr-1" size="20">mdi-video</v-icon>
          <template v-if="event.me_purchased || event.me_purchased_video || !event.video_price"> Посмотреть </template>
          <template v-else>
            {{ event.video_price }}
            <v-icon class="ml-half" size="16">hand</v-icon>
          </template>
        </v-button>
        <v-button
          v-else-if="event.me_purchased"
          mode="success"
          @click.stop="emit('action', { type: 'event-open', id: event.id })"
        >
          Вы участник
        </v-button>
        <v-button
          v-else-if="event.me_coach"
          mode="primary"
          @click.stop="emit('action', { type: 'event-open', id: event.id })"
        >
          Открыть
        </v-button>
        <v-button
          v-else-if="!event.me_locked && !isPassed"
          mode="primary"
          @click.stop="emit('action', { type: 'event-purchase', id: event.id })"
        >
          {{ event.price ? 'Купить' : 'Записаться' }}
        </v-button>
        <div v-else style="width: 150px" />
      </div>
    </td>
  </tr>
</template>

<style lang="scss">
.webinar-calendar {
  &__list-event-row {
    // table at the start to override specificity
    table > &_parents-allowed > td:first-child {
      border-left-color: $blue;
    }

    cursor: pointer;

    // Timings column
    & > td:first-child {
      min-width: 180px;
      border-left: 4px $orange solid;
      padding: 8px 16px;
    }

    // Body column
    & > td:nth-child(2) {
      width: 100%;
      border-right: none;
    }

    // Actions column
    & > td:nth-child(3) {
      border-left: none;
      padding-right: 16px;
    }
  }

  // 1. Timings column
  &__list-event-timings {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(2, auto);
    grid-template-rows: repeat(2, auto);
    grid-template-areas:
      'date date'
      'time duration';

    font-weight: 400;
    font-size: 14px;
    line-height: 16px;

    @include mobile {
      padding-right: 16px;
    }
  }

  &__list-event-date {
    grid-area: date;
  }
  &__list-event-time {
    grid-area: time;
  }
  &__list-event-duration {
    grid-area: duration;
  }

  &__list-event-date,
  &__list-event-time,
  &__list-event-duration {
    display: flex;
    align-items: center;
  }

  &__list-event-duration > div {
    display: inline-block;
    padding: 4px 12px;

    background-color: $lightenBlue;
    border-radius: 4px;

    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
  }

  // 2. Body column
  &__list-event-body {
    width: 100%;
    padding: 6px 16px;

    display: flex;
  }

  &__list-event-info {
    flex: 1;

    display: flex;
    flex-direction: column;
  }

  &__list-event-name {
    @include text-body-bold;
    margin-bottom: 4px;
  }

  &__list-event-badge {
    @include text-caption;
    margin-top: 4px;
    padding: 4px 8px;

    display: inline-block;

    border-radius: 4px;
    background: #ffeaec;

    & > a {
      color: $blue;
    }
  }

  &__list-event-price {
    @include text-body-bold;
    flex: 0;

    display: flex;
    align-items: center;
  }

  &__list-fav {
    color: $gray;

    &_active {
      color: $red;
    }
  }

  // 3. Actions column
  &__list-event-actions {
    display: flex;
    white-space: nowrap;
    align-items: center;

    & > * + * {
      margin-left: 12px;
    }

    @include mobile {
      padding: 8px 0;
      justify-content: space-between;
    }

    @include desktop {
      justify-content: flex-end;
      & > .v-button:last-child {
        width: 150px;
      }
    }
  }

  &__list-fav-btn {
    flex: 0;
  }

  &__list-passed {
    @include text-body-bold;
    color: $darkGray;

    @include desktop {
      text-align: right;
      flex: 1;
    }
    @include mobile {
      text-align: center;
    }
  }

  // Mobile view
  &__list-event-mobile {
    display: flex;
    flex-direction: column;
  }
}
</style>
